import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Users, Crown, TrendUp, ForkKnife } from "@phosphor-icons/react";

export default function Admin() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
                setStats(s.data); setUsers(u.data);
            } catch (e) { setErr(formatApiError(e)); }
        })();
    }, []);

    const filtered = users.filter((u) =>
        !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-white" data-testid="admin-page">
            <Navbar />
            <div className="mx-auto max-w-7xl px-6 py-8">
                <div className="mb-8">
                    <div className="uppercase-tag">Console admin</div>
                    <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Pilotage FrigoZero</h1>
                </div>

                {err && <div className="text-fzred text-sm mb-4" data-testid="admin-error">{err}</div>}

                {stats && (
                    <div className="grid md:grid-cols-4 gap-4 mb-8">
                        <KPI testid="kpi-users" icon={<Users size={20} />} label="Utilisateurs" value={stats.total_users}
                            sub={`+${stats.new_users_7d} cette semaine`} />
                        <KPI testid="kpi-premium" icon={<Crown size={20} />} label="Premium" value={stats.premium_users}
                            sub={`${stats.total_users ? Math.round(100 * stats.premium_users / stats.total_users) : 0}% conversion`} />
                        <KPI testid="kpi-actions" icon={<TrendUp size={20} />} label="Actions totales" value={stats.total_actions}
                            sub={`${stats.total_consumed} sauvés · ${stats.total_wasted} gaspillés`} />
                        <KPI testid="kpi-items" icon={<ForkKnife size={20} />} label="Aliments trackés" value={stats.total_fridge_items}
                            sub={`${stats.estimated_global_impact.kg_saved} kg économisés`} />
                    </div>
                )}

                <div className="card-flat">
                    <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
                        <div className="uppercase-tag">Utilisateurs inscrits</div>
                        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher email ou nom..." className="ml-auto border border-zinc-300 px-3 py-2 text-sm w-72" />
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="admin-users-table">
                            <thead className="bg-zinc-50 uppercase-tag text-left">
                                <tr>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Nom</th>
                                    <th className="px-4 py-3">Rôle</th>
                                    <th className="px-4 py-3">Premium</th>
                                    <th className="px-4 py-3">Créé le</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((u) => (
                                    <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                                        <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                                        <td className="px-4 py-3">{u.name}</td>
                                        <td className="px-4 py-3">
                                            <span className={"text-[10px] font-mono uppercase px-2 py-0.5 " + (u.role === "admin" ? "bg-zinc-950 text-white" : "bg-zinc-100")}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.is_premium
                                                ? <span className="text-[10px] font-mono uppercase px-2 py-0.5 bg-fzblue text-white">Premium</span>
                                                : <span className="text-[10px] text-zinc-400">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">Aucun utilisateur trouvé.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function KPI({ icon, label, value, sub, testid }) {
    return (
        <div data-testid={testid} className="card-flat p-6">
            <div className="flex items-center gap-2 text-fzblue">{icon}<span className="uppercase-tag">{label}</span></div>
            <div className="font-display font-black text-4xl mt-2">{value}</div>
            <div className="text-xs text-zinc-500 mt-1">{sub}</div>
        </div>
    );
}
